/* Frontend copy of shrd utils/helpers/messenger/signature.helper.ts (keep in sync) */
import {SEND_OCCASIONS, SendOccasion} from '@/enums/messenger/SendOccasion.enum';

/**
 * "Unterschrift" of mails / WhatsApp messages (placeholder senderName), set per occasion in "Absender & Anlässe"
 * (SenderOccasionSettings.signature), overridable per user ("Als mein Standard speichern" / the send dialog).
 * Only templates that contain {{senderName}} show it – fixed texts ("Ihr Team von mymusicschool") stay as they are.
 */
export enum SignatureMode {
    FIRST_NAME = 'firstName',
    FULL_NAME = 'fullName',
    TEAM = 'team',
}

/** Missing setting = team */
export const DEFAULT_SIGNATURE_MODE = SignatureMode.TEAM;

export const SIGNATURE_MODE_LABELS: Record<SignatureMode, string> = {
    [SignatureMode.FIRST_NAME]: 'Vorname',
    [SignatureMode.FULL_NAME]: 'Vor- und Nachname',
    [SignatureMode.TEAM]: 'Team',
};

export const TEAM_SIGNATURE_FORMAL = 'Ihr Team von mymusicschool';
export const TEAM_SIGNATURE_INFORMAL = 'Dein Team von mymusicschool';

/** Recipients addressed formally ("Sie"): parents, Standorte, customers, schools; everybody else informally ("du") */
const FORMAL_RECIPIENTS = ['Eltern', 'Standorte', 'Kund:innen', 'Schulen'];

export function isSignatureMode(value: unknown): value is SignatureMode {
    return typeof value === 'string' && (Object.values(SignatureMode) as string[]).includes(value);
}

/** "Ihr Team von mymusicschool" (parents, Standorte, customers) / "Dein Team von mymusicschool" (teachers, applicants …) */
export function teamSignature(occasion: SendOccasion | string | undefined): string {
    const to = occasion ? SEND_OCCASIONS[occasion as SendOccasion]?.to : undefined;
    return to && FORMAL_RECIPIENTS.includes(to) ? TEAM_SIGNATURE_FORMAL : TEAM_SIGNATURE_INFORMAL;
}

/** Effective mode: personal choice > setting of the occasion > team */
export function effectiveSignatureMode(occasionSetting?: unknown, personal?: unknown): SignatureMode {
    if (isSignatureMode(personal)) {
        return personal;
    }
    return isSignatureMode(occasionSetting) ? occasionSetting : DEFAULT_SIGNATURE_MODE;
}

export type SignaturePerson = { firstName?: string | null; lastName?: string | null } | null | undefined;

/** Text of the signature; a personal mode without a name (system, cron) falls back to the team */
export function signatureText(mode: SignatureMode | string | undefined, person: SignaturePerson, occasion: SendOccasion | string | undefined): string {
    const first = (person?.firstName || '').trim();
    const last = (person?.lastName || '').trim();
    if (mode === SignatureMode.FIRST_NAME && first) {
        return first;
    }
    if (mode === SignatureMode.FULL_NAME && (first || last)) {
        return [first, last].filter(Boolean).join(' ');
    }
    return teamSignature(occasion);
}

/** Setting of the occasion from the messenger answer (`channels.sender-areas`); older messenger / missing = team */
export function occasionSignatureMode(settings: { occasions?: Array<{ occasion?: string; signature?: string | null }> | null } | null | undefined,
                                      occasion: SendOccasion | string): SignatureMode {
    const item = (settings?.occasions || []).find((o) => o?.occasion === occasion);
    return effectiveSignatureMode(item?.signature);
}

type WaComponentLike = { type?: string; text?: string; example?: { vanillaReferences?: unknown[] } };

/**
 * The WhatsApp template signs "{{senderName}} und das Team" – the team text would read "Dein Team von mymusicschool und
 * das Team": there "Team" is not selectable and falls back to the first name.
 */
export function whatsAppTeamBlocked(components: unknown): boolean {
    const body = (Array.isArray(components) ? components as WaComponentLike[] : []).find((c) => c?.type === 'BODY');
    const index = (body?.example?.vanillaReferences || []).indexOf('senderName');
    if (!body?.text || index < 0) {
        return false;
    }
    const at = body.text.indexOf(`{{${index + 1}}}`);
    return at >= 0 && /^\s*und das Team/i.test(body.text.slice(at + `{{${index + 1}}}`.length));
}

/** Mode for a WhatsApp template: team falls back to the first name where the template appends "und das Team" */
export function whatsAppSignatureMode(mode: SignatureMode, components: unknown): SignatureMode {
    return mode === SignatureMode.TEAM && whatsAppTeamBlocked(components) ? SignatureMode.FIRST_NAME : mode;
}
